import "babel-polyfill"
import { List, Map, Set } from "immutable"
import * as React from "react"
import * as ReactDOM from "react-dom"
import { Route, Router, Switch } from "react-router-dom"
import "url-search-params-polyfill"
import "whatwg-fetch"
import { async, AsyncState, unloadedAsyncState } from "widgets-for-react"
import { EventRoutes } from "../../../Shared/components/Events/EventRoutes"
import { EventsApplicationState, initialEventsApplicationState } from "../../../Shared/components/Events/EventState"
import { features, setFeatures } from "../../../Shared/features"
import { CookieStatus, getCookieStatus } from "../../../Shared/utils/datalayer.mapping"
import { ApiResult, getRegions, Region } from "../../custom_api/api"
import { Branch } from "../../custom_api/wise/types"
import * as DrupalModel from '../../custom_models/drupal'
import { getCookie } from "../../custom_utils/cookies"
import { FramedCookiebar } from "./cookiebar"
import { getCurrentFilterParams, setFilterParam } from "./filters"
import { Footer } from "./footer"
import { Header } from "./header"
import { history } from "./history"
import { InspirationRoutes } from "./Inspiration/InspirationRoutes"
import { initialInspirationState, InspirationState } from "./Inspiration/InspirationState"
import { Main } from "./library/main"
import { trackActivitySelection } from "./library/tracking"
import { initialLocalizationState, LocalizationState, PREF_BRANCH_COOKIE } from "./Localization/LocalizationState"
import { PageRenderer } from "./page_renderer"
import { PeriodSlug } from "./region_and_date_filter"

interface ApplicationProps { }

export type ApplicationState = {
  readonly routes: Set<string>
  readonly regions: ApiResult<Map<string, Region>>
  readonly cookieStatus: CookieStatus
  readonly showArchive: boolean
  readonly localizationState: LocalizationState
} & BookarangState
  & EventsApplicationState

export type BookarangState = {
  readonly inspirationState: InspirationState
  readonly isKioskMode: boolean
  readonly inspirationRoutePath: string
} & BranchState

export interface BranchState {
  branches: AsyncState<List<Branch>>
  branch: 'none' | Branch
}

const applicationStateZero: ApplicationState = {
  routes: Set(["/", "/nieuwsbrief"]),
  regions: "loading",
  cookieStatus: getCookieStatus(),
  showArchive: false,
  inspirationState: initialInspirationState,
  localizationState: initialLocalizationState,
  isKioskMode: false,
  inspirationRoutePath: '',
  branch: 'none',
  branches: unloadedAsyncState(),
  ...initialEventsApplicationState
}

const KIOSK_MODE_PAGES = ['/bookarang-pilot']

class Application extends React.Component<ApplicationProps, ApplicationState> {
  constructor(props: ApplicationProps) {
    super(props)

    this.state = applicationStateZero

    this.selectRegion = this.selectRegion.bind(this)
    this.selectPeriod = this.selectPeriod.bind(this)
    this.resetPeriod = this.resetPeriod.bind(this)
    this.selectLocation = this.selectLocation.bind(this)
  }

  componentDidMount() {
    const s = this.state

    const isKioskMode = KIOSK_MODE_PAGES.findIndex(item => new RegExp(item).test(history.location.pathname)) >= 0

    this.setState({ isKioskMode, inspirationRoutePath: isKioskMode ? 'bookarang-pilot' : 'collectie' })

    if (s.regions == "loading") {
      getRegions().then(r => this.setState((s, _) => ({ ...s, regions: r })))
    }
  }

  selectRegion(e: React.ChangeEvent<HTMLSelectElement>) {
    e.stopPropagation()
    setFilterParam('waar', e.target.value)
    trackActivitySelection("where", e.target.value)
  }

  selectPeriod(e: React.ChangeEvent<HTMLSelectElement>) {
    e.stopPropagation()
    setFilterParam('wanneer', e.target.value)
  }

  resetPeriod() {
    setFilterParam('wanneer', 'elk moment')
  }

  selectLocation(selectedLocationId: string) {
    this.setState((s0) => ({ ...s0, localizationState: { ...s0.localizationState, selectedLocationId: selectedLocationId } }))
  }

  render() {
    const branchLocations = this.state.localizationState.branchLocations.kind == 'loaded' ? this.state.localizationState.branchLocations.value : []

    const regions = this.state.regions

    const pageRenderer = (slug: string, params?: string) => {
      let filterParams = getCurrentFilterParams()

      return (
        <>
          {async<DrupalModel.Location[]>()(this.state.localizationState.branchLocations).run(u => this.setState((s0) => (
            { ...s0, localizationState: { ...s0.localizationState, branchLocations: u(s0.localizationState.branchLocations) } }
          )))}
          <PageRenderer
            route={slug}
            params={params}
            regions={regions}
            selectedPeriod={filterParams.has('wanneer') ? filterParams.get('wanneer') as PeriodSlug : 'any'}
            selectedRegion={filterParams.has('waar') ? filterParams.get('waar') : ''}
            selectedCluster={filterParams.has('categorie') ? filterParams.get('categorie') : ''}
            selectPeriod={this.selectPeriod}
            selectRegion={this.selectRegion}
            resetPeriod={this.resetPeriod}
            toggleArchive={cb => this.setState(s => ({ ...s, showArchive: !s.showArchive }), () => (cb ? cb() : null))}
            showArchive={this.state.showArchive}
            selectedLocationId={this.state.localizationState.selectedLocationId}
            branchLocations={branchLocations}
            selectLocation={this.selectLocation}
          />
        </>
      )
    }

    const branchId = getCookie(PREF_BRANCH_COOKIE)
    const homePath = (branchId && branchId != "0") ? `/node/${branchId}` : '/home'
    const eventConfig = features().events

    return (
      <Router history={history}>
        {
          this.state.isKioskMode
            ? <>
              <Main>
                <Switch>
                  {InspirationRoutes({ state: this.state, setState: this.setState.bind(this) })}

                  <Route exact key="page" path="/:slug*" render={props => pageRenderer(props.location.pathname, props.location.search)} />
                </Switch>
              </Main></>
            : <>
              <FramedCookiebar
                status={this.state.cookieStatus}
                onChange={(cookieStatus: CookieStatus) => this.setState(s => ({ ...s, cookieStatus }))}
              />
              <Header />
              <Main>
                <Switch>
                  <Route exact key="home" path="/" render={props => pageRenderer(homePath, props.location.search)} />

                  {InspirationRoutes({ state: this.state, setState: this.setState.bind(this) })}

                  {eventConfig != 'off' && EventRoutes({
                    state: this.state,
                    config: eventConfig,
                    setState: this.setState.bind(this),
                    eventsOverviewClassNames: {
                      filterCheckbox: "a-filter__check form__check",
                      filterCheckboxInput: "",
                      filterCheckboxLabel: "",
                      timelineButton: "quicklink__link",
                      jumboSize: "large"
                    }
                  })}

                  <Route exact key="page" path="/:slug*" render={props => pageRenderer(props.location.pathname, props.location.search)} />
                </Switch>
              </Main>
              <Footer onCookieChange={(cookieStatus: CookieStatus) => this.setState(s => ({ ...s, cookieStatus }))} />
            </>
        }
      </Router>
    )
  }
}

export const render: (library: string) => void = (l) => {
  setFeatures(l)
  ReactDOM.render(
    <Application/>,
    document.getElementById("react-content"),
    () => {removeLoader()}
  )
}

function removeLoader() {
  const loader = document.documentElement.querySelector('#js--loader');
  if (loader == null) return;
  loader.remove();
}
